import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SERIAL_LONG_RE,
  composeSerialLong,
  deriveSerialShortFromLong,
  productionWeekFromDate,
} from '@bhops/shared';
import type { ListModelsResponse, ModelSummaryDto } from '@bhops/shared';
import {
  productionPlansApi,
  type MaterializeRobotSpec,
  type ProductionPlan,
} from '@/api/production-plans';
import { modelsApi } from '@/api/models';
import { extractApiError } from '@/api/client';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input } from '@/components/Form';
import { useToast } from '@/components/Toast';

// FB2 §7B (결정 E) — 운영자가 plan 행의 "완성 처리" 버튼으로 여는 모달.
// 제조 계획을 실제 Robot row(status READY_TO_SHIP)로 머티리얼라이즈한다.
// 백엔드 `materialize` 엔드포인트(POST /production-plans/:id/materialize)는
// 이미 존재 — 본 모달은 그 body(`{ robots: MaterializeRobotSpec[],
// autoReserveContract? }`)를 구성해 호출한다.
//
// 결정 E 요약: cron 자동화(완성예정일 도래 시 무인 생성)는 보류하고, 사람이
// "완성 확인 후" 누르는 수동 버튼을 1차로 채택(무마이그레이션·최저 리스크).
// 추후 ETA-cron 을 옵션 레이어로 얹을 수 있다(plan 플래그 마이그레이션 후).
//
// 모달 구성:
//   1) 상단 — plan spec 읽기 전용(모델 표시명 / 색상 / 불감지 / 구분 사양 / 완성 예정일)
//   2) 생산일자(공통) — 기본값은 plan.estimatedCompletionAt. Long-SN 의 생산주(YYWW)
//      산출 + 각 로봇 manufactureCompletedAt/firstProductionYm 에 사용.
//   3) 수량 — 기본값 = 남은 수량(plannedCount - materializedCount), 1..남은수량.
//   4) 로봇별 행 N개 — Short SN + (PC모듈 → Long-SN 자동조립) / Long-SN 직접 입력.
//      색상·불감지·구분사양은 plan spec 을 서버가 상속하므로 입력받지 않는다.
//
// SN 부여: 색상·불감지를 plan spec 으로 고정한 채 PC 모듈만 받아
// `composeSerialLong` 으로 Long-SN 을 조립(로봇 폼과 동일 컴포저). 모델
// 고정 ColorMode(예: Zephyr 'S2')도 데이터 주도로 반영. 규격 외 케이스는
// 행별 "직접" 토글로 원시 Long-SN 입력. SN 은 모두 선택(빈 값 → null).

interface Props {
  open: boolean;
  onClose: () => void;
  plan: ProductionPlan;
}

interface RobotRow {
  serialShort: string;
  // 직접입력(manual=true) 모드의 원시 Long-SN. 컴포저 모드에선 무시되고
  // pcModule + plan spec 으로 매 렌더 조립된다(staleness 방지).
  manualSerialLong: string;
  pcModule: string; // 컴포저 입력(말미 일련번호). DB 컬럼 아님.
  manual: boolean; // true → manualSerialLong 직접 입력, 컴포저 미사용
  // round5 #3b — 사용자가 Short SN 을 직접 수정하면 PC모듈→Short 자동
  // 동기화를 멈춘다(레거시 예외 허용).
  shortDirty: boolean;
}

function emptyRow(): RobotRow {
  return { serialShort: '', manualSerialLong: '', pcModule: '', manual: false, shortDirty: false };
}

const SENSORLESS_PREFIXES = ['SEN', 'HL'];

export function MaterializeModal({ open, onClose, plan }: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  const materialized = plan.materializedCount ?? 0;
  const remaining = Math.max(0, plan.plannedCount - materialized);

  // Round 2 Phase F (#8) — 편집 중 raw string. 매 키 입력마다
  // Math.max(1, Number()||1) 로 clamp 하면 빈칸·다자리 입력이 막혀 1 로
  // 튕긴다. 표시값은 countStr, 파생 숫자 count 는 행 개수·검증·제출에 쓰고
  // blur 에서만 [1, remaining] 으로 보정한다.
  const [countStr, setCountStr] = useState<string>(String(Math.max(1, remaining)));
  const [productionDate, setProductionDate] = useState<string>('');
  const [autoReserve, setAutoReserve] = useState<boolean>(true);
  const [rows, setRows] = useState<RobotRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 파생 수량(빈칸 → 0). 행 개수·검증·footer 라벨에서 사용.
  const count = Math.max(0, Math.floor(Number(countStr) || 0));

  // 모델 메타(shortPrefix / fixedColorMode) — Long-SN 조립에 필요. 로봇 폼·
  // 생성 모달과 동일 queryKey 로 react-query 디듀프(추가 네트워크 없음).
  const modelsQ = useQuery<ListModelsResponse>({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    enabled: open,
  });
  const model: ModelSummaryDto | undefined = modelsQ.data?.items.find((m) => m.id === plan.modelId);

  // 모달 열릴 때마다 초기화. plan.id 변경 시에도(같은 모달 인스턴스 재사용) 리셋.
  useEffect(() => {
    if (!open) return;
    const cap = Math.max(0, plan.plannedCount - (plan.materializedCount ?? 0));
    setCountStr(String(Math.max(1, cap)));
    // 생산일자 기본값 = plan 완성 예정일(YYYY-MM-DD). 운영자가 실제 완성일로 조정.
    // 추가수정 2차(26-07-27) 정정 — 위 "완성 예정일" 표시를 KST 로 바꿨으므로
    // 기본값도 같은 기준으로 뽑는다(보이는 날짜와 채워지는 날짜가 어긋나면
    // 시리얼의 생산주까지 하루 밀린다). date input 규격이라 하이픈 유지.
    setProductionDate(kstYmd(plan.estimatedCompletionAt)?.replace(/\./g, '-') ?? '');
    setAutoReserve(true);
    setError(null);
  }, [open, plan.id, plan.plannedCount, plan.materializedCount, plan.estimatedCompletionAt]);

  // 수량 변경 시 행 배열 길이를 맞춘다(기존 입력은 보존, 부족분만 추가/초과분 제거).
  useEffect(() => {
    if (!open) return;
    setRows((prev) => {
      const n = Math.max(0, Math.min(count, remaining));
      if (prev.length === n) return prev;
      if (prev.length < n) return [...prev, ...Array.from({ length: n - prev.length }, emptyRow)];
      return prev.slice(0, n);
    });
    // remaining 은 plan 의존 — count/open 변화에만 반응(remaining 은 그 시점 값 사용).
  }, [count, open, remaining]);

  // 생산주(YYWW) — 생산일자에서 산출. Long-SN 조립 + 미리보기에 사용.
  const productionWeek = useMemo(() => {
    if (!productionDate) return '';
    const d = new Date(productionDate);
    return Number.isNaN(d.getTime()) ? '' : productionWeekFromDate(d);
  }, [productionDate]);

  const modelPrefix = model?.shortPrefix ?? '';
  const prefixUnusable = modelPrefix === '' || modelPrefix === '-';
  const sensorless = SENSORLESS_PREFIXES.includes(modelPrefix);
  const fixedColorMode = model?.fixedColorMode ?? null;

  // 주어진 PC모듈로 Long-SN 을 조립(색상·불감지는 plan spec 고정). 조립 불가 시 ''.
  // 모델 메타(prefix/fixedColorMode)·생산주에 의존하므로 매 렌더 호출해 staleness
  // 를 방지한다(모델 쿼리가 늦게 resolve 되거나 생산일자가 바뀌어도 자동 반영).
  function composeFor(pcModule: string): string {
    if (prefixUnusable || productionWeek === '' || pcModule.trim() === '') return '';
    return composeSerialLong({
      modelPrefix,
      productionWeek,
      color: plan.color ?? undefined,
      sensorType: plan.sensorType ?? undefined,
      pcModule: pcModule.trim(),
      sensorless,
      fixedColorMode: fixedColorMode ?? undefined,
    });
  }

  // 행의 최종(effective) Long-SN. manual 이면 직접입력 값, 아니면 조립 결과.
  // 미리보기·검증·제출에서 모두 이 함수를 사용해 단일 진실원을 보장한다.
  function effectiveLong(row: RobotRow): string {
    return row.manual ? row.manualSerialLong.trim() : composeFor(row.pcModule);
  }

  function updateRow(idx: number, patch: Partial<RobotRow>): void {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  const materialize = useMutation({
    mutationFn: () => {
      const isoDate = productionDate
        ? new Date(`${productionDate}T09:00:00.000Z`).toISOString()
        : undefined;
      // R2 후속(워크플로 H3) — Robot.firstProductionYm 전 시스템 컨벤션은 4자리 YYMM
      // (toYearMonthYY). YYYY-MM 으로 보내면 제조 KPI 집계(/^\d{4}$/)에서 누락되고
      // 목록 '첫 제조 월' 표시도 깨진다. productionDate(YYYY-MM-DD) → YYMM.
      const firstYm = productionDate
        ? `${productionDate.slice(2, 4)}${productionDate.slice(5, 7)}`
        : undefined; // YYMM
      const robots: MaterializeRobotSpec[] = rows.map((r) => {
        const serialLong = effectiveLong(r);
        const serialShort = r.serialShort.trim();
        return {
          ...(serialShort ? { serialShort } : {}),
          ...(serialLong ? { serialLong } : {}),
          ...(isoDate ? { manufactureCompletedAt: isoDate } : {}),
          ...(firstYm ? { firstProductionYm: firstYm } : {}),
        };
      });
      return productionPlansApi.materialize(plan.id, { robots, autoReserveContract: autoReserve });
    },
    onSuccess: (res) => {
      // counts/상태가 바뀌므로 plan 목록 + 로봇 목록 + 제조 대시보드 무효화.
      qc.invalidateQueries({ queryKey: ['production-plans'] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      const reserved = res.reservedContractIds.length;
      toast.success(
        `${res.materializedCount}대를 완성 처리해 출고 대기로 로봇 목록에 올렸습니다.` +
          (reserved > 0 ? ` 계약 ${reserved}건을 예약했습니다.` : ''),
      );
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (remaining <= 0) {
      setError('이 계획은 더 이상 완성 처리할 수량이 없습니다.');
      return;
    }
    if (count < 1) {
      setError('완성 수량은 1 이상이어야 합니다.');
      return;
    }
    if (count > remaining) {
      setError(`남은 수량 ${remaining}대를 넘을 수 없습니다.`);
      return;
    }
    // 최종 Long-SN(조립 또는 직접입력)이 있으면 형식 검증(빈 값은 허용 — SN 미부여).
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row) continue;
      const sl = effectiveLong(row);
      if (sl !== '' && !SERIAL_LONG_RE.test(sl)) {
        setError(`${i + 1}번 로봇의 Long SN 형식이 올바르지 않습니다: ${sl}`);
        return;
      }
    }
    materialize.mutate();
  }

  const disabled = remaining <= 0;

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!materialize.isPending) onClose();
      }}
      title={`${plan.humanId} 완성 처리`}
      width={620}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={materialize.isPending}
          >
            닫기
          </Button>
          <Button
            type="submit"
            form="materialize-form"
            disabled={disabled || materialize.isPending || count < 1}
          >
            {materialize.isPending ? '완성 처리 중…' : `${count}대 완성 처리`}
          </Button>
        </>
      }
    >
      <form
        id="materialize-form"
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <FormError>{error}</FormError>

        {/* plan spec — 읽기 전용. 생성될 로봇이 이 spec(색상/불감지/구분사양)을 상속. */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
            padding: 12,
            background: 'var(--bg-sunken)',
            borderRadius: 'var(--r-card)',
            fontSize: 12,
          }}
        >
          <SpecKV label="모델" value={plan.model?.displayName ?? plan.modelId} />
          {/* 추가수정 2차(26-07-27) 정정 — 제조 계획 목록과 같은 완성 예정일.
              결정 ②A 대로 KST 날짜 표기로 통일. */}
          <SpecKV label="완성 예정일" value={kstYmd(plan.estimatedCompletionAt) ?? '–'} />
          <SpecKV label="색상" value={plan.color ?? '—'} />
          <SpecKV label="불감지" value={plan.sensorType ?? '—'} />
          <SpecKV label="구분 사양" value={plan.variantValue ?? '—'} />
          <SpecKV
            label="계획 / 완성 / 남음"
            value={`${plan.plannedCount} / ${materialized} / ${remaining}`}
          />
        </section>

        {disabled && (
          <div
            style={{
              padding: 8,
              fontSize: 12,
              background: 'var(--bg-info-subtle)',
              border: '1px solid var(--blue-200)',
              borderRadius: 'var(--r-card)',
              color: 'var(--blue-800)',
            }}
          >
            이 계획은 이미 모두 완성 처리되었습니다.
          </div>
        )}

        {/* 공통 입력 — 생산일자 + 수량 + 자동 예약 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field
            label="생산일자"
            htmlFor="mat-date"
            hint={`생산주 ${productionWeek || '—'}. 완성일과 생산월로 기록됩니다.`}
          >
            <Input
              id="mat-date"
              type="date"
              value={productionDate}
              onChange={(e) => setProductionDate(e.target.value)}
              disabled={disabled}
            />
          </Field>
          <Field label="완성 수량" htmlFor="mat-count" required hint={`최대 ${remaining}대`}>
            <Input
              id="mat-count"
              type="number"
              min={1}
              max={remaining}
              value={countStr}
              onChange={(e) => setCountStr(e.target.value)}
              onBlur={() =>
                setCountStr((s) =>
                  String(Math.max(1, Math.min(remaining, Math.floor(Number(s) || 1)))),
                )
              }
              disabled={disabled}
              required
            />
          </Field>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={autoReserve}
            onChange={(e) => setAutoReserve(e.target.checked)}
            disabled={disabled}
          />
          <span>
            계약 자동 예약. 이 계획에 할당된 계약이 있으면 만든 로봇을 설치일이 가까운 계약부터
            예약합니다. 끄면 로봇만 만들고 예약은 나중에 직접 합니다.
          </span>
        </label>

        {/* 로봇별 SN 입력 */}
        {!disabled && (
          <section>
            <div className="kuun-label" style={{ marginBottom: 6 }}>
              로봇 SN 입력 ({rows.length})
            </div>
            {prefixUnusable && (
              <div
                style={{
                  padding: 8,
                  fontSize: 12,
                  marginBottom: 8,
                  background: 'var(--bg-sunken)',
                  border: '1px dashed var(--border-default)',
                  borderRadius: 'var(--r-card)',
                  color: 'var(--fg-muted)',
                }}
              >
                이 모델은 Long SN을 자동으로 만들 수 없습니다. 각 행의 "직접 입력"을 켜서 Long SN을
                적거나 SN 없이 만들어 주세요.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map((r, idx) => {
                const composedPreview = !r.manual ? composeFor(r.pcModule) : '';
                const previewInvalid =
                  composedPreview !== '' && !SERIAL_LONG_RE.test(composedPreview);
                return (
                  <div
                    key={idx}
                    style={{
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--r-card)',
                      padding: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600 }}>로봇 #{idx + 1}</span>
                      <label
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}
                      >
                        <input
                          type="checkbox"
                          checked={r.manual}
                          onChange={(e) => updateRow(idx, { manual: e.target.checked })}
                          aria-label={`로봇 ${idx + 1} Long SN 직접 입력`}
                        />
                        직접 입력
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Field
                        label="Short SN"
                        htmlFor={`mat-short-${idx}`}
                        hint="PC 모듈을 넣으면 자동으로 채워지고, 직접 고칠 수도 있습니다."
                      >
                        <Input
                          id={`mat-short-${idx}`}
                          value={r.serialShort}
                          onChange={(e) =>
                            // round5 #3b — 자동값과 다른 직접 입력은 dirty 로
                            // 기록해 이후 자동 동기화를 멈춘다.
                            updateRow(idx, {
                              serialShort: e.target.value,
                              shortDirty:
                                e.target.value.trim() !==
                                (deriveSerialShortFromLong(effectiveLong(r)) ?? ''),
                            })
                          }
                          placeholder="예: 2113"
                          style={{ fontFamily: 'var(--font-mono)' }}
                        />
                      </Field>
                      {r.manual ? (
                        <Field
                          label="Long SN"
                          htmlFor={`mat-long-${idx}`}
                          hint="비워 둬도 됩니다. 예: GEN2-2613-WF207"
                        >
                          <Input
                            id={`mat-long-${idx}`}
                            value={r.manualSerialLong}
                            onChange={(e) => updateRow(idx, { manualSerialLong: e.target.value })}
                            placeholder="예: GEN2-2613-WF207"
                            style={{ fontFamily: 'var(--font-mono)' }}
                          />
                        </Field>
                      ) : (
                        <Field
                          label="PC 모듈"
                          htmlFor={`mat-pc-${idx}`}
                          hint={
                            composedPreview
                              ? `만들어질 SN: ${composedPreview}${previewInvalid ? '. 형식을 확인해 주세요.' : ''}`
                              : 'SN 끝의 일련번호입니다. 예: 207. 비우면 SN 없이 만듭니다.'
                          }
                        >
                          <Input
                            id={`mat-pc-${idx}`}
                            value={r.pcModule}
                            onChange={(e) => {
                              // round5 #3b — PC모듈 → Short SN 자동 동기화
                              // (사용자가 직접 수정하기 전까지). 파생 규칙은
                              // Long SN 구조 기반(deriveSerialShortFromLong)
                              // 이라 Zephyr '2' 접두도 자동 반영.
                              const pcModule = e.target.value;
                              const derived = deriveSerialShortFromLong(composeFor(pcModule) || '');
                              updateRow(idx, {
                                pcModule,
                                ...(r.shortDirty || !derived ? {} : { serialShort: derived }),
                              });
                            }}
                            placeholder="예: 207"
                            inputMode="numeric"
                          />
                        </Field>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="kuun-caption" style={{ color: 'var(--fg-muted)', fontSize: 11 }}>
          완성 처리하면 로봇이 <strong>출고 대기</strong> 상태로 로봇 목록에 추가되고, 색상과
          불감지, 구분 사양은 계획을 그대로 따릅니다. 완성 예정일이 되어도 자동으로 만들어지지
          않으니 여기서 직접 완성 처리해 주세요.
        </div>
      </form>
    </Modal>
  );
}

function SpecKV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: 'var(--fg-muted)' }}>{label}</div>
      <div style={{ fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}
